import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { Device } from '@core/utilities/device';
import { Subscription } from 'rxjs';

@Component({
  selector: 'scrollbar',
  templateUrl: './scrollbar.component.html',
  styleUrls: ['./scrollbar.component.scss']
})
export class ScrollbarComponent implements OnInit, OnDestroy {

  private _pendingTarget: string;
  private _min: number = 20;
  private _observer = new MutationObserver(() => this.render());

  constructor(private element: ElementRef, private layout: LayoutService) {    
  }

  @Input()
  public set target(domOrId: HTMLElement | string) {

    if (this.scrollable) {
      this.scrollable.scrollEventDispatcher().removeEventListener('scroll', this.onscroll);
      this.scrollable = null;
    }

    if (typeof domOrId == 'string' && !this.scrollbar) {
      this._pendingTarget = domOrId as string;
    }
    else {
      this._pendingTarget = null;
      this.scrollable = typeof domOrId == 'string' ? this.scrollbar.topElement().querySelector('#' + domOrId) : domOrId;

      if (this.scrollable) {
        this.scrollable = this.scrollable.scrollableElement();
        this.scrollable.scrollEventDispatcher().addEventListener('scroll', this.onscroll);
        this._observer.observe(this.scrollable, { childList: true, subtree: true });
        this.onscroll();
      }
    }    
  }

  @Input()
  public set min(value: number) {
    this._min = value;

    if (this.scrollbar && this.scrollable) {
      this.render();
    }
  }

  public get min() {
    return this._min;
  }

  private scrollable: HTMLElement;
  private thumb: HTMLDivElement;
  private scrollbar: HTMLElement;
  private vertical: boolean;

  private mouse: number = NaN;
  private marked: { mouse: number, position: number } = null;

  private onmousedown = e => {

    if (!this.scrollable || Device.mobile) return;

    this.marked = { mouse: this.mouse, position: this.vertical ? this.scrollable.scrollTop : this.scrollable.scrollLeft }
    this.scrollbar.classList.add('active');
    e.stopPropagation();
  }

  private onmouseup = e => {

    if (!this.scrollable) return;

    this.marked = null;
    this.scrollbar.classList.remove('active');
    e.stopPropagation();
  }

  private onmousemove = e => {

    if (!this.scrollable || Device.mobile) return;

    this.mouse = this.vertical ? e.clientY - this.scrollbar.offsetTop : e.clientX - this.scrollbar.offsetLeft;
    if (this.marked) {
      if (this.vertical) {
        this.scrollable.scrollTop = Math.round(this.marked.position + (this.mouse - this.marked.mouse) * this.scrollable.scrollHeight / this.scrollbar.clientHeight);
      }
      else {
        this.scrollable.scrollLeft = Math.round(this.marked.position + (this.mouse - this.marked.mouse) * this.scrollable.scrollWidth / this.scrollbar.clientWidth);
      }
    }
  }

  private onclick = e => {

    if (!this.scrollable || Device.mobile) return;

    if (e.target == this.scrollbar) {
      if (this.vertical) {
        this.scrollable.scrollTop = Math.round((this.mouse - this.thumb.clientHeight / 2) * this.scrollable.scrollHeight / this.scrollbar.clientHeight);
      }
      else {
        this.scrollable.scrollLeft = Math.round((this.mouse - this.thumb.clientWidth / 2) * this.scrollable.scrollWidth / this.scrollbar.clientWidth);
      }
    }
    e.stopPropagation();
  }

  private onscroll = () => {

    this.layout.change.emit({ type: 'scroll', scope: this.scrollable });
    this.render();
  }

  private render() {

    if (Device.mobile || !this.scrollable || !this.scrollbar) return;

    if ((this.vertical && this.scrollable.clientHeight == this.scrollable.scrollHeight) || (!this.vertical && this.scrollable.clientWidth == this.scrollable.scrollWidth)) {
      this.scrollbar.classList.add('hidden');
    }
    else {
      this.scrollbar.classList.remove('hidden');

      if (this.vertical) {

        let cH = Math.round(this.scrollable.clientHeight * this.scrollbar.clientHeight / this.scrollable.scrollHeight);
        let rH = Math.max(cH, this._min);
        let rT = Math.round(this.scrollable.scrollTop * (this.scrollbar.clientHeight - rH + cH) / this.scrollable.scrollHeight);

        this.thumb.style.height = rH + "px";
        this.thumb.style.top = rT + "px";
      }
      else {

        let cW = Math.round(this.scrollable.clientWidth * this.scrollbar.clientWidth / this.scrollable.scrollWidth);
        let rW = Math.max(cW, this._min);
        let rL = Math.round(this.scrollable.scrollLeft * (this.scrollbar.clientWidth - rW + cW) / this.scrollable.scrollWidth);

        this.thumb.style.width = rW + "px";
        this.thumb.style.left = rL + "px";
      }
    }      
  }

  private subscription: Subscription;

  ngOnInit() {
    
    this.scrollbar = this.element.nativeElement as HTMLElement;
    this.thumb = this.scrollbar.children[0] as HTMLDivElement;

    this.scrollbar.classList.add('hidden');

    if (!['left', 'right', 'top', 'bottom'].first(i => this.scrollbar.classList.contains(i), null)) this.scrollbar.classList.add('right');
    this.vertical = ['left', 'right'].first(i => this.scrollbar.classList.contains(i), null) != null;

    if (!Device.mobile) {
      window.addEventListener('mousemove', this.onmousemove);
      window.addEventListener('mouseup', this.onmouseup);
      this.thumb.addEventListener('mousedown', this.onmousedown);
      this.scrollbar.addEventListener('click', this.onclick);
      this.subscription = this.layout.resize.subscribe(() => this.onscroll());
    }

    if (this._pendingTarget) {
      this.target = this._pendingTarget;
    }

    if (!this.scrollable) {
      this.target = this.scrollbar.parentElement;
    }
  }

  ngOnDestroy() {

    this.target = null;

    if (!Device.mobile) {

      window.removeEventListener('mousemove', this.onmousemove);
      window.removeEventListener('mouseup', this.onmouseup);
      this.thumb.removeEventListener('mousedown', this.onmousedown);
      this.scrollbar.removeEventListener('click', this.onclick);
      this.subscription.unsubscribe();
    }

    this._observer.disconnect();
  }
}
